/**
 * WordPress dependencies
 */
import { dispatch } from '@safe-wordpress/data';
import { _x } from '@safe-wordpress/i18n';
import { store as NOTICES } from '@safe-wordpress/notices';

/**
 * External dependencies
 */
import { omit } from 'lodash';
import { v4 as uuid } from 'uuid';
import type {
	Alternative,
	AlternativeId,
	Goal,
	Segment,
	ScopeRule,
} from '@nab/types';

export function createAlternative(
	id?: AlternativeId
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Alternative< Record< string, any > > {
	return {
		id: id ?? uuid(),
		attributes: {},
		base: 'control',
		links: { edit: '', preview: '', heatmap: '' },
	};
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function duplicateAlternative< T extends Record< string, any > >(
	alternative: Alternative< T >
): Alternative< T > {
	return {
		...omit( alternative, 'ai' ),
		id: uuid(),
		base: alternative.id,
		links: {
			edit: '',
			heatmap: '',
			preview: '',
		},
	};
}

export function createGoal(): Goal {
	return {
		id: uuid(),
		attributes: { name: '' },
		conversionActions: [],
	};
}

export function createSegment(): Segment {
	return {
		id: uuid(),
		attributes: { name: '' },
		segmentationRules: [],
	};
}

export function createScopeRule(
	attributes: ScopeRule[ 'attributes' ]
): ScopeRule {
	return { id: uuid(), attributes } as ScopeRule;
}

export function createStagingNotice(
	staging: 'environment-type' | 'url' | false
): boolean {
	const { createWarningNotice } = dispatch( NOTICES ) || {};
	switch ( staging ) {
		case 'url':
			void createWarningNotice(
				_x(
					'This site looks like a staging site and, therefore, Nelio A/B Testing won’t track any events.',
					'text',
					'nelio-ab-testing'
				) +
					' ' +
					_x(
						'If this is not correct, please use the following filter to fix it:',
						'user',
						'nelio-ab-testing'
					),
				{
					isDismissible: false,
					actions: [
						{
							label: 'nab_staging_urls',
							url: 'https://neliosoftware.com/testing/hooks/nab_staging_urls/',
						},
					],
				}
			);
			return true;

		case 'environment-type':
			void createWarningNotice(
				_x(
					'This site is a staging site and, therefore, Nelio A/B Testing won’t track any events.',
					'text',
					'nelio-ab-testing'
				),
				{ isDismissible: false }
			);
			return true;

		case false:
			return false;
	}
}
